import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Search } from 'lucide-react';
import { adminApi, productsApi } from '../../lib/api';

const CATEGORIES = ['matte', 'satin', 'sheer', 'gloss'];

const STATUS_STYLE = {
  active: 'bg-[#e6efe1] border-[#b7ceac] text-[#3d5a2a]',
  scheduled: 'bg-[#e2e8f3] border-[#aebfd8] text-[#2a3d5a]',
  expired: 'bg-[#f3e3d8] border-[#d8b49a] text-[#7a3a2a]',
  disabled: 'bg-[#ece4d2] border-[#cdc2b1] text-[#7a6a5a]',
};

const emptyCoupon = () => ({
  code: '',
  description: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_cart_value: 0,
  max_discount: '',
  starts_at: '',
  expires_at: '',
  total_limit: '',
  per_user_limit: '',
  first_order_only: false,
  customer_eligibility: 'everyone',
  customer_user_ids: [],
  product_eligibility: 'all',
  product_ids: [],
  category_ids: [],
  excluded_product_ids: [],
  payment_method: 'all',
  visibility: 'public',
  stackable: false,
  enabled: true,
});

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toISO = (local) => (local ? new Date(local).toISOString() : null);

const Toggle = ({ checked, onChange, label, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className={`flex items-center gap-3 text-sm ${checked ? 'text-[#1a1410]' : 'text-[#7a6a5a]'}`}>
    <span className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${checked ? 'bg-[#1a1410]' : 'bg-[#cdc2b1]'}`}>
      <span className={`w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
    {label}
  </button>
);

const Lbl = ({ children }) => <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a] block mb-1">{children}</label>;
const inputCls = 'w-full bg-white border border-[#cdc2b1] px-3 py-2 text-sm focus:outline-none focus:border-[#1a1410]';

const CouponsTab = () => {
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([adminApi.listCoupons(), productsApi.list()]);
      setCoupons(c);
      setProducts(p);
    } catch (_) {
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editing || editing.customer_eligibility !== 'selected') return;
    const t = setTimeout(async () => {
      try { setUserResults(await adminApi.listUsers(userSearch.trim())); } catch (_) { setUserResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch, editing]);

  const openNew = () => { setErr(''); setEditing(emptyCoupon()); };
  const openEdit = (c) => {
    setErr('');
    setEditing({
      ...emptyCoupon(),
      ...c,
      max_discount: c.max_discount ?? '',
      total_limit: c.total_limit ?? '',
      per_user_limit: c.per_user_limit ?? '',
      starts_at: toLocalInput(c.starts_at),
      expires_at: toLocalInput(c.expires_at),
    });
  };

  const toggleArr = (key, id) => {
    const arr = editing[key] || [];
    setEditing({ ...editing, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const payload = {
        ...editing,
        code: editing.code.trim().toUpperCase(),
        discount_value: parseFloat(editing.discount_value) || 0,
        min_cart_value: parseFloat(editing.min_cart_value) || 0,
        max_discount: editing.max_discount === '' ? null : parseFloat(editing.max_discount),
        total_limit: editing.total_limit === '' ? null : parseInt(editing.total_limit),
        per_user_limit: editing.per_user_limit === '' ? null : parseInt(editing.per_user_limit),
        starts_at: toISO(editing.starts_at),
        expires_at: toISO(editing.expires_at),
      };
      delete payload.id; delete payload.status; delete payload.remaining;
      delete payload.used_count; delete payload.total_discount_given;
      delete payload.created_at; delete payload.updated_at;
      if (editing.id) await adminApi.updateCoupon(editing.id, payload);
      else await adminApi.createCoupon(payload);
      setEditing(null);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (c) => {
    const payload = {
      ...c,
      enabled: !c.enabled,
      max_discount: c.max_discount ?? null,
      total_limit: c.total_limit ?? null,
      per_user_limit: c.per_user_limit ?? null,
    };
    delete payload.id; delete payload.status; delete payload.remaining;
    delete payload.used_count; delete payload.total_discount_given;
    delete payload.created_at; delete payload.updated_at;
    await adminApi.updateCoupon(c.id, payload);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this coupon? This cannot be undone.')) return;
    await adminApi.deleteCoupon(id);
    await load();
  };

  const valueLabel = (c) => {
    if (c.discount_type === 'percentage') return `${c.discount_value}%`;
    if (c.discount_type === 'fixed') return `Rs. ${c.discount_value}`;
    return 'Free Shipping';
  };

  return (
    <div className="mt-8" data-testid="admin-coupons-tab">
      <div className="flex justify-end mb-6">
        <button onClick={openNew} data-testid="new-coupon-btn" className="inline-flex items-center gap-2 bg-[#1a1410] text-[#f5f1e6] px-6 py-3 text-[11px] tracking-[0.32em] hover:bg-black">
          <Plus className="w-4 h-4" strokeWidth={1.6} /> NEW COUPON
        </button>
      </div>

      <div className="overflow-x-auto border border-[#e5dfd0]">
        <table className="w-full text-sm">
          <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
            <tr>
              <th className="p-4">CODE</th>
              <th className="p-4">DISCOUNT</th>
              <th className="p-4">STATUS</th>
              <th className="p-4">USED / REMAINING</th>
              <th className="p-4">TOTAL GIVEN</th>
              <th className="p-4">VISIBILITY</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#7a6a5a]">Loading…</td></tr>
            ) : coupons.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#7a6a5a]">No coupons yet.</td></tr>
            ) : coupons.map((c) => (
              <tr key={c.id} className="border-t border-[#e5dfd0] align-top" data-testid={`coupon-row-${c.id}`}>
                <td className="p-4">
                  <div className="font-mono font-medium">{c.code}</div>
                  {c.description ? <div className="text-xs text-[#7a6a5a]">{c.description}</div> : null}
                </td>
                <td className="p-4">{valueLabel(c)}</td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase border ${STATUS_STYLE[c.status] || ''}`} data-testid={`coupon-status-${c.id}`}>{c.status}</span>
                </td>
                <td className="p-4 text-xs">{c.used_count} / {c.remaining == null ? '∞' : c.remaining}</td>
                <td className="p-4 text-xs tabular-nums">Rs. {(c.total_discount_given || 0).toFixed(2)}</td>
                <td className="p-4 text-xs uppercase">{c.visibility}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => toggleEnabled(c)} data-testid={`toggle-coupon-${c.id}`} className="text-[11px] tracking-[0.18em] mr-4 hover:text-black">
                    {c.enabled ? 'DISABLE' : 'ENABLE'}
                  </button>
                  <button onClick={() => openEdit(c)} className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] mr-4 hover:text-black">
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.4} /> EDIT
                  </button>
                  <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] text-[#7a3a2a] hover:text-[#5a1a0a]">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /> DELETE
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="coupon-form"
            className="bg-[#f5f1e6] border border-[#cdc2b1] max-w-2xl w-full p-8 my-8">
            <div className="flex items-start justify-between mb-6">
              <h3 className="font-serif text-3xl">{editing.id ? 'Edit' : 'New'} coupon</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-[#7a6a5a] hover:text-black"><X className="w-5 h-5" /></button>
            </div>

            {err && <div className="mb-5 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Lbl>COUPON CODE</Lbl>
                <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} required data-testid="coupon-code-input" className={`${inputCls} font-mono`} />
              </div>
              <div>
                <Lbl>DESCRIPTION</Lbl>
                <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} />
              </div>

              <div>
                <Lbl>DISCOUNT TYPE</Lbl>
                <select value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })} data-testid="coupon-type-select" className={inputCls}>
                  <option value="percentage">Percentage %</option>
                  <option value="fixed">Fixed Rs.</option>
                  <option value="free_shipping">Free Shipping</option>
                </select>
              </div>
              <div>
                <Lbl>DISCOUNT VALUE</Lbl>
                <input type="number" step="0.01" min="0" value={editing.discount_value} disabled={editing.discount_type === 'free_shipping'}
                  onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} data-testid="coupon-value-input" className={`${inputCls} disabled:opacity-50`} />
              </div>

              <div>
                <Lbl>MINIMUM CART VALUE</Lbl>
                <input type="number" step="0.01" min="0" value={editing.min_cart_value} onChange={(e) => setEditing({ ...editing, min_cart_value: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Lbl>MAX DISCOUNT (blank = none)</Lbl>
                <input type="number" step="0.01" min="0" value={editing.max_discount} disabled={editing.discount_type !== 'percentage'}
                  onChange={(e) => setEditing({ ...editing, max_discount: e.target.value })} className={`${inputCls} disabled:opacity-50`} />
              </div>

              <div>
                <Lbl>START DATE/TIME</Lbl>
                <input type="datetime-local" value={editing.starts_at} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Lbl>EXPIRY DATE/TIME</Lbl>
                <input type="datetime-local" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} className={inputCls} />
              </div>

              <div>
                <Lbl>TOTAL USAGE LIMIT (blank = unlimited)</Lbl>
                <input type="number" min="1" value={editing.total_limit} onChange={(e) => setEditing({ ...editing, total_limit: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Lbl>PER-USER LIMIT (blank = unlimited)</Lbl>
                <input type="number" min="1" value={editing.per_user_limit} onChange={(e) => setEditing({ ...editing, per_user_limit: e.target.value })} className={inputCls} />
              </div>

              <div>
                <Lbl>PAYMENT METHOD</Lbl>
                <select value={editing.payment_method} onChange={(e) => setEditing({ ...editing, payment_method: e.target.value })} className={inputCls}>
                  <option value="all">All</option>
                  <option value="prepaid">Prepaid Only</option>
                  <option value="cod">COD Only</option>
                </select>
              </div>
              <div>
                <Lbl>VISIBILITY</Lbl>
                <select value={editing.visibility} onChange={(e) => setEditing({ ...editing, visibility: e.target.value })} className={inputCls}>
                  <option value="public">Public</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>

              <div>
                <Lbl>CUSTOMER ELIGIBILITY</Lbl>
                <select value={editing.customer_eligibility} onChange={(e) => setEditing({ ...editing, customer_eligibility: e.target.value })} className={inputCls}>
                  <option value="everyone">Everyone</option>
                  <option value="new">New Customers</option>
                  <option value="existing">Existing Customers</option>
                  <option value="selected">Selected Users</option>
                </select>
              </div>
              <div>
                <Lbl>PRODUCT ELIGIBILITY</Lbl>
                <select value={editing.product_eligibility} onChange={(e) => setEditing({ ...editing, product_eligibility: e.target.value })} className={inputCls}>
                  <option value="all">All Products</option>
                  <option value="selected">Selected Products</option>
                </select>
              </div>
            </div>

            {editing.customer_eligibility === 'selected' && (
              <div className="mt-5">
                <Lbl>SELECT USERS ({editing.customer_user_ids.length} selected)</Lbl>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6a5a]" strokeWidth={1.6} />
                  <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search name or phone…" className={`${inputCls} pl-9`} />
                </div>
                <div className="max-h-40 overflow-y-auto border border-[#cdc2b1] divide-y divide-[#e5dfd0]">
                  {userResults.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[#efe8d8]">
                      <input type="checkbox" checked={editing.customer_user_ids.includes(u.id)} onChange={() => toggleArr('customer_user_ids', u.id)} />
                      <span>{u.name || 'Unnamed'}</span>
                      <span className="text-xs text-[#7a6a5a] font-mono ml-auto">{u.phone || u.email || ''}</span>
                    </label>
                  ))}
                  {userResults.length === 0 && <div className="px-3 py-2 text-xs text-[#7a6a5a]">Search to find users.</div>}
                </div>
              </div>
            )}

            {editing.product_eligibility === 'selected' && (
              <div className="mt-5">
                <Lbl>ELIGIBLE PRODUCTS ({editing.product_ids.length} selected)</Lbl>
                <div className="max-h-40 overflow-y-auto border border-[#cdc2b1] divide-y divide-[#e5dfd0]">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[#efe8d8]">
                      <input type="checkbox" checked={editing.product_ids.includes(p.id)} onChange={() => toggleArr('product_ids', p.id)} />
                      <span>{p.name} <span className="text-[#7a6a5a]">— {p.shade}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5">
              <Lbl>CATEGORY ELIGIBILITY (none = all categories)</Lbl>
              <div className="flex flex-wrap gap-3">
                {CATEGORIES.map((cat) => (
                  <label key={cat} className={`px-4 py-2 border text-[11px] tracking-[0.18em] uppercase cursor-pointer ${editing.category_ids.includes(cat) ? 'border-[#1a1410] bg-[#ece4d2]' : 'border-[#cdc2b1]'}`}>
                    <input type="checkbox" className="hidden" checked={editing.category_ids.includes(cat)} onChange={() => toggleArr('category_ids', cat)} />
                    {cat}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <Lbl>EXCLUDED PRODUCTS ({editing.excluded_product_ids.length} selected)</Lbl>
              <div className="max-h-32 overflow-y-auto border border-[#cdc2b1] divide-y divide-[#e5dfd0]">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[#efe8d8]">
                    <input type="checkbox" checked={editing.excluded_product_ids.includes(p.id)} onChange={() => toggleArr('excluded_product_ids', p.id)} />
                    <span>{p.name} <span className="text-[#7a6a5a]">— {p.shade}</span></span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-6">
              <Toggle checked={editing.first_order_only} onChange={(v) => setEditing({ ...editing, first_order_only: v })} label="First order only" testid="coupon-first-order-toggle" />
              <Toggle checked={editing.stackable} onChange={(v) => setEditing({ ...editing, stackable: v })} label="Allow stacking" testid="coupon-stack-toggle" />
              <Toggle checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} label="Enabled" testid="coupon-enabled-toggle" />
            </div>

            <div className="mt-8 flex gap-4">
              <button type="submit" disabled={busy} data-testid="save-coupon-btn" className="bg-[#1a1410] text-[#f5f1e6] px-8 py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
                {busy ? 'SAVING…' : 'SAVE COUPON'}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">CANCEL</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CouponsTab;
